import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ScrollText, Plus, Filter, ArrowRight, AlertCircle, Calendar } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

const CATEGORIES = [
  { key: "ALL", label: "All" },
  { key: "PETS", label: "Pets" },
  { key: "PARKING", label: "Parking" },
  { key: "SHORT_STAY", label: "Short-stay" },
  { key: "COMMON_PROPERTY", label: "Common property" },
  { key: "NOISE", label: "Noise" },
  { key: "OTHER", label: "Other" },
];

const STATUS_STYLE = {
  ACTIVE: { bg: "#dcfce7", color: "#166534" },
  AMENDED: { bg: "var(--blue-light)", color: "var(--blue-dark)" },
  PROPOSED: { bg: "#fef3c7", color: "#92400e" },
  REPEALED: { bg: "#fee2e2", color: "#991b1b" },
};

const CATEGORY_LABEL = {
  PETS: "Pets",
  PARKING: "Parking",
  SHORT_STAY: "Short-stay",
  COMMON_PROPERTY: "Common property",
  NOISE: "Noise",
  OTHER: "Other",
};

function StatusPill({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.ACTIVE;
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold uppercase tracking-wider"
      style={{ background: s.bg, color: s.color }}
      data-testid={`bylaw-status-${status}`}
    >
      {status}
    </span>
  );
}

export default function Bylaws() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [schemeId, setSchemeId] = useState(getActiveSchemeId());
  const [members, setMembers] = useState([]);
  const [bylaws, setBylaws] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");

  // Determine my role on the active scheme
  useEffect(() => {
    let mounted = true;
    (async () => {
      let id = schemeId;
      if (!id) {
        try {
          const { data: list } = await api.get("/schemes");
          if (!list.length) {
            navigate("/onboarding");
            return;
          }
          id = list[0].scheme_id;
          if (mounted) setSchemeId(id);
        } catch (e) {
          if (mounted) setError(apiError(e));
          return;
        }
      }
      try {
        const [{ data: m }, { data: bls }] = await Promise.all([
          api.get(`/schemes/${id}/members`),
          api.get(`/schemes/${id}/bylaws`),
        ]);
        if (!mounted) return;
        setMembers(m);
        setBylaws(bls);
      } catch (e) {
        if (mounted) setError(apiError(e));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const myMembership = members.find((mm) => mm.user_id === user?.user_id);
  const myRole = myMembership?.role;
  const isOwner = myRole === "owner" && user?.system_role !== "platform_admin";
  const canWrite = ["chair", "committee"].includes(myRole) || user?.system_role === "platform_admin";

  // Owners see only ACTIVE + AMENDED by default
  const allowedStatuses = isOwner
    ? ["ACTIVE", "AMENDED"]
    : ["ACTIVE", "AMENDED", "PROPOSED", "REPEALED"];

  const filtered = useMemo(() => {
    return bylaws.filter((b) => {
      if (categoryFilter !== "ALL" && b.category !== categoryFilter) return false;
      if (statusFilter !== "ALL" && b.status !== statusFilter) return false;
      return true;
    });
  }, [bylaws, categoryFilter, statusFilter]);

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8" data-testid="bylaws-page">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <div>
            <h1
              className="text-2xl font-bold tracking-tight flex items-center gap-2"
              style={{ color: "var(--navy)" }}
              data-testid="bylaws-heading"
            >
              <ScrollText size={24} style={{ color: "var(--blue)" }} />
              By-laws register
            </h1>
            <p className="text-sm mt-1" style={{ color: "var(--slate)" }}>
              {isOwner
                ? "Currently in force at this scheme."
                : "Complete history of by-laws — active, proposed, amended, and repealed."}
            </p>
          </div>
          {canWrite && (
            <Link to="/scheme/bylaws/new">
              <Button
                size="sm"
                className="h-9"
                style={{ background: "var(--blue)", color: "white" }}
                data-testid="bylaws-new-button"
              >
                <Plus size={14} className="mr-1.5" />
                New by-law
              </Button>
            </Link>
          )}
        </div>

        {error && (
          <div
            className="rounded-lg p-4 text-sm mb-6 flex items-center gap-2"
            style={{ background: "var(--alert-light)", color: "var(--alert)" }}
            data-testid="bylaws-error"
          >
            <AlertCircle size={16} />
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="space-y-3 mb-5" data-testid="bylaws-filters">
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <Filter size={14} style={{ color: "var(--slate)" }} className="shrink-0" />
            <span className="text-[11px] uppercase tracking-wider font-semibold shrink-0" style={{ color: "var(--slate)" }}>
              Category
            </span>
            {CATEGORIES.map((c) => {
              const isActive = categoryFilter === c.key;
              return (
                <button
                  key={c.key}
                  onClick={() => setCategoryFilter(c.key)}
                  data-testid={`bylaws-cat-filter-${c.key}`}
                  className="text-xs px-3 py-1.5 rounded-full border whitespace-nowrap font-medium transition-colors"
                  style={{
                    borderColor: isActive ? "var(--blue)" : "var(--border)",
                    background: isActive ? "var(--blue-light)" : "white",
                    color: isActive ? "var(--blue-dark)" : "var(--slate)",
                  }}
                >
                  {c.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-2 overflow-x-auto pb-1">
            <span className="ml-5 text-[11px] uppercase tracking-wider font-semibold shrink-0" style={{ color: "var(--slate)" }}>
              Status
            </span>
            {["ALL", ...allowedStatuses].map((s) => {
              const isActive = statusFilter === s;
              const count = s === "ALL" ? bylaws.length : bylaws.filter((b) => b.status === s).length;
              return (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  data-testid={`bylaws-status-filter-${s}`}
                  className="text-xs px-3 py-1.5 rounded-full border whitespace-nowrap font-medium transition-colors"
                  style={{
                    borderColor: isActive ? "var(--blue)" : "var(--border)",
                    background: isActive ? "var(--blue-light)" : "white",
                    color: isActive ? "var(--blue-dark)" : "var(--slate)",
                  }}
                >
                  {s === "ALL" ? "All" : s.replace("_", " ")}
                  <span className="ml-1.5 opacity-70">({count})</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* List */}
        <div
          className="rounded-xl bg-white border overflow-hidden"
          style={{ borderColor: "var(--border)" }}
        >
          {loading ? (
            <div className="p-5 space-y-3">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          ) : filtered.length === 0 ? (
            <div className="px-5 py-16 text-center" data-testid="bylaws-empty">
              <ScrollText size={32} className="mx-auto mb-3" style={{ color: "var(--slate)" }} />
              <p className="text-sm font-medium" style={{ color: "var(--navy)" }}>
                No by-laws match these filters
              </p>
              <p className="text-xs mt-1" style={{ color: "var(--slate)" }}>
                {canWrite ? "Use \u201cNew by-law\u201d above to draft one." : "Check back later."}
              </p>
            </div>
          ) : (
            <ul className="divide-y" style={{ borderColor: "var(--border)" }} data-testid="bylaws-list">
              {filtered.map((b) => (
                <li key={b.bylaw_id} data-testid={`bylaw-row-${b.bylaw_id}`}>
                  <Link
                    to={`/scheme/bylaws/${b.bylaw_id}`}
                    className="flex items-center justify-between gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="text-xs font-mono font-semibold px-2 py-0.5 rounded"
                          style={{ background: "#f1f5f9", color: "var(--navy)" }}
                        >
                          #{b.by_law_number}
                        </span>
                        <span className="text-sm font-semibold truncate" style={{ color: "var(--navy)" }}>
                          {b.title}
                        </span>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium"
                          style={{ background: "#f1f5f9", color: "var(--slate)" }}
                        >
                          {CATEGORY_LABEL[b.category] || b.category}
                        </span>
                        <StatusPill status={b.status} />
                      </div>
                      <div className="text-xs mt-1 flex items-center gap-3 flex-wrap" style={{ color: "var(--slate)" }}>
                        {b.effective_date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={11} />
                            Effective {String(b.effective_date).slice(0, 10)}
                          </span>
                        )}
                        {b.amended_date && (
                          <span>Amended {String(b.amended_date).slice(0, 10)}</span>
                        )}
                        {b.repealed_date && (
                          <span>Repealed {String(b.repealed_date).slice(0, 10)}</span>
                        )}
                      </div>
                    </div>
                    <ArrowRight size={14} style={{ color: "var(--slate)" }} className="shrink-0" />
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </AppShell>
  );
}
